(() => {
  const MIRROR_COOKIE = 'satellai_consent';
  const MIRROR_MAX_AGE = 60 * 60 * 24 * 365;

  /**
   * 调整 Shopify 原生 Customer Privacy 横幅的前台文案。
   * 同意状态由 Shopify 原生脚本保存，并同步到 Google Consent Mode。
   */
  class SatellaiShopifyPrivacyBanner {
    constructor() {
      this.observer = null;
      this.handleClick = this.handleClick.bind(this);
      this.handleConsentChange = this.handleConsentChange.bind(this);
    }

    init() {
      document.addEventListener('click', this.handleClick);
      document.addEventListener('visitorConsentCollected', this.handleConsentChange);
      this.syncExistingConsent();
      if (this.applyBannerText()) {
        this.applyPreferencesText();
        return;
      }

      // Shopify 的横幅是异步注入的，找到后立即停止观察，避免长期监听 DOM。
      this.observer = new MutationObserver(() => {
        if (!this.applyBannerText()) return;
        this.applyPreferencesText();
        this.observer.disconnect();
        this.observer = null;
      });
      this.observer.observe(document.body, { childList: true, subtree: true });
    }

    applyBannerText() {
      const settingsButton = document.getElementById('shopify-pc__banner__btn-manage-prefs');
      const acceptButton = document.getElementById('shopify-pc__banner__btn-accept');
      const declineButton = document.getElementById('shopify-pc__banner__btn-decline');
      if (!settingsButton || !acceptButton || !declineButton) return false;

      this.setButtonText(settingsButton, 'Cookie Settings');
      this.setButtonText(declineButton, 'Reject all');
      this.setButtonText(acceptButton, 'Accept all');
      return true;
    }

    applyPreferencesText() {
      const acceptButton = document.getElementById('shopify-pc__prefs__header-accept');
      if (acceptButton) this.setButtonText(acceptButton, 'Accept all');
    }

    /** 优先更新 Shopify 按钮内的 span，保留原有 DOM 结构和焦点样式。 */
    setButtonText(button, text) {
      const textElement = button.querySelector('span') || button;
      if (textElement.textContent.trim() !== text) textElement.textContent = text;
    }

    handleClick(event) {
      const settingsButton = event.target.closest('#shopify-pc__banner__btn-manage-prefs');
      if (!settingsButton) return;

      // 偏好弹窗可能在点击后才生成，下一帧再更新其按钮文案。
      requestAnimationFrame(() => this.applyPreferencesText());
    }

    /**
     * global 站页面首次访问时保持默认拒绝；若用户曾经做过选择，
     * 仅把 Shopify 明确记录为 yes 的类别恢复为同意。
     */
    syncExistingConsent() {
      if (!window.Shopify || typeof window.Shopify.loadFeatures !== 'function') return;

      window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (error) => {
        if (error) return;
        const privacy = window.Shopify.customerPrivacy;
        if (!privacy || typeof privacy.currentVisitorConsent !== 'function') return;

        try {
          const consent = privacy.currentVisitorConsent();
          const values = [consent.analytics, consent.marketing, consent.preferences];
          const hasExplicitChoice = values.some((value) => value === 'yes' || value === 'no');
          if (!hasExplicitChoice) return;

          this.updateConsentMode({
            analytics: consent.analytics === 'yes',
            marketing: consent.marketing === 'yes',
            preferences: consent.preferences === 'yes'
          });
        } catch (error) {
          // Shopify API 读取失败时继续保持 global 站的默认拒绝状态
        }
      });
    }

    /** Shopify 在用户保存同意选择后会发出该事件。 */
    handleConsentChange(event) {
      const consent = event.detail;
      if (!consent) return;

      this.updateConsentMode({
        analytics: consent.analyticsAllowed === true,
        marketing: consent.marketingAllowed === true,
        preferences: consent.preferencesAllowed === true
      });
    }

    /** 同步 Google Consent Mode，并写入可在下次首帧读取的镜像 Cookie。 */
    updateConsentMode(choices) {
      this.writeMirrorCookie(choices);
      window.dataLayer = window.dataLayer || [];
      if (typeof window.gtag !== 'function') {
        window.gtag = function () {
          window.dataLayer.push(arguments);
        };
      }

      window.gtag('consent', 'update', {
        ad_storage: choices.marketing ? 'granted' : 'denied',
        ad_user_data: choices.marketing ? 'granted' : 'denied',
        ad_personalization: choices.marketing ? 'granted' : 'denied',
        analytics_storage: choices.analytics ? 'granted' : 'denied',
        functionality_storage: choices.preferences ? 'granted' : 'denied',
        personalization_storage: choices.preferences ? 'granted' : 'denied'
      });

      window.dataLayer.push({
        event: 'satellai_consent_update',
        consent_granted: choices.analytics && choices.marketing && choices.preferences,
        consent_analytics: choices.analytics,
        consent_marketing: choices.marketing,
        consent_preferences: choices.preferences,
        consent_source: 'shopify_native_banner'
      });
    }

    writeMirrorCookie(choices) {
      const payload = {
        v: 1,
        s: { a: choices.analytics, m: choices.marketing, p: choices.preferences },
        t: Date.now()
      };

      try {
        const value = encodeURIComponent(JSON.stringify(payload));
        const secure = window.location.protocol === 'https:' ? '; Secure' : '';
        document.cookie =
          MIRROR_COOKIE + '=' + value + '; Path=/; Max-Age=' + MIRROR_MAX_AGE + '; SameSite=Lax' + secure;
      } catch (error) {
        // 镜像 Cookie 写入失败不影响 Shopify 保存的真实同意结果
      }
    }
  }

  const start = () => {
    const banner = new SatellaiShopifyPrivacyBanner();
    banner.init();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    requestAnimationFrame(start);
  }
})();
